import * as React from "react";
import { MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { SuccessIcon } from "components/icons/SuccessIcon";
import FailedView from "components/import-file-dialog/FailedView";
import ImportFileDialog, { DialogState } from "components/import-file-dialog/ImportFileDialog";
import ImportKeysFailedDialog from "components/import-file-dialog/ImportKeysFailedDialog";
import LoadingView from "components/import-file-dialog/LoadingView";
import SelectingView from "components/import-file-dialog/SelectingView";
import layoutStyle from "components/layout/layout.scss";
import { OEM_LICENSE_ID } from "components/licenses/common";
import { SerialLicenseKey } from "components/licenses/ViewImportedLicenseKeys";
import { renameFilename } from "components/reports/import/ImportReportsDialog";
import { LicenseData } from "domain/licenses";
import { hasLicenseSerialKey } from "services/licenses/licenseRepository";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { StoreState } from "store";
import { setHasLicenseSerialKey } from "store/license";
import { formatDateWithoutTime } from "utils/format";
import { Logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

interface Props {
    onClose: () => void;
    fileList?: File[];
    totalAvailableLicenses: number;
    availableLicenses: LicenseData[];
    onTitleChange?: (title: string) => void;
    oemLicenseExpirationDate?: string;
    onLicenseImportCallback: (
        oemLicenseKeys: SerialLicenseKey[],
        oemFailedKeys: SerialLicenseKey[],
        licenseKeyErrors: Map<string, LicenseKeyError>,
        assignLicensesCountFromCsv: number,
        selectedFileListReference: MutableRefObject<File[] | undefined>
    ) => void;
}
const connector = connect((state: StoreState) => ({
    theme: state.themeReducer.theme,
}));

export interface LicenseKeyError {
    licenseKey: string;
    failingFields: string[];
}
const LOGGER = new Logger("ImportLicenseKeyDialog");

const LICENSE_KEY_PATTERN = /^[A-Za-z0-9-]+$/;

export const MAX_KEY_LENGTH = 1024;
export const MAX_IDENTIFIER_LENGTH = 36;

const ImportLicenseKeyDialog = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [dialogState, setDialogState] = React.useState(DialogState.SELECTING_FILE);

    const [error, setError] = React.useState<{ field: string; message: string } | string | undefined>(undefined);
    const [oemLicenseKeys, setOemLicenseKeys] = React.useState<SerialLicenseKey[]>([]);
    const [oemFailedKeys, setOemFailedKeys] = React.useState<SerialLicenseKey[]>([]);
    const selectedFileListReference = React.useRef<File[] | undefined>(props.fileList);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const hasSucceeded = React.useRef(false);
    const dispatchSerialKey = useDispatch();
    const filenameToFile = React.useRef<Map<string, File> | null>(null);

    if (filenameToFile.current == null) {
        filenameToFile.current = convertFilenames(props.fileList ?? []);
    }

    React.useEffect(() => {
        const availableLicense = props.availableLicenses.find((license) => license.type === OEM_LICENSE_ID);
        if (availableLicense && assignLicensesCountFromCsv > availableLicense.available) {
            setOemFailedKeys(oemLicenseKeys);
            props.onTitleChange?.(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.uploadingKeysFailed"));
            setDialogState(DialogState.LICENSE_KEY_CREATION_FAILED);
        }
    }, []);

    function convertFilenames(files: File[]): Map<string, File> {
        const filenames = new Map<string, File>();
        for (const each of files) {
            const originalFilename = each.name;
            const filename = renameFilename(originalFilename);
            if (filenames.has(filename)) {
                LOGGER.debug(
                    `Duplicate derived filename "${filename}" so skipping file. Size: ${each.size}. Original filename: "${originalFilename}".`
                );
                continue;
            }
            filenames.set(filename, each);
        }
        return filenames;
    }

    function getExpirationDate() {
        const availableLicense = props.availableLicenses.find((license) => license.type === OEM_LICENSE_ID);
        if (availableLicense?.expirationDate) {
            return availableLicense.expirationDate;
        }
        return "";
    }

    function isUTCFormat(dateString: string): boolean {
        const utcPattern =
            /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)|(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?[+-]\d{2}:\d{2})$/;
        return utcPattern.test(dateString);
    }

    function isDateValid(expirationDate: string, dateToValidate: string): boolean {
        if (!expirationDate) {
            return false;
        }
        const expDate = new Date(expirationDate);
        const dateToBeValidated = new Date(formatDateWithoutTime(dateToValidate));
        const today = new Date();

        const expDateStr = `${expDate.getFullYear()}-${(expDate.getMonth() + 1).toString().padStart(2, "0")}-${expDate.getDate().toString().padStart(2, "0")}`;
        const dateString = `${dateToBeValidated.getFullYear()}-${(dateToBeValidated.getMonth() + 1).toString().padStart(2, "0")}-${dateToBeValidated.getDate().toString().padStart(2, "0")}`;
        const todayStr = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`;

        return dateString >= todayStr && dateString <= expDateStr;
    }

    function checkOemLicenseAvailable() {
        let licenseSerialKeyAvailable = false;
        licenseSerialKeyAvailable = dispatchSerialKey(setHasLicenseSerialKey(hasLicenseSerialKey())).payload;
        return licenseSerialKeyAvailable;
    }
    let assignLicensesCountFromCsv = 0;

    const parseCSV = (result: string) => {
        setDialogState(DialogState.LOADING_FILE);
        const lines = result.split("\n");
        const headers = lines[0].split(",").map((header) => header.trim());
        const requiredHeaders = [
            t("ViewOemKeys.table.licenseKeyHeader"),
            t("ViewOemKeys.table.assignCountHeader"),
            t("ViewOemKeys.table.identifierHeader"),
            t("ViewOemKeys.table.startDateHeader"),
            t("ViewOemKeys.table.expirationDateHeader"),
            t("ViewOemKeys.table.licenseIdHeader"),
        ];

        if (!checkOemLicenseAvailable()) {
            setDialogState(DialogState.IMPORTING_FAILED);
            setError(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.oemLicenseNotAvailable"));
            LOGGER.error(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.oemLicenseNotAvailable"));
            throw new Error(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.oemLicenseNotAvailable"));
        }
        const currentFailedKeys: string[] = [];

        const licenseKeyErrors = new Map<string, LicenseKeyError>();
        const missingHeaders = requiredHeaders.filter((requiredHeader) => !headers.includes(requiredHeader));
        if (missingHeaders.length > 0) {
            setDialogState(DialogState.IMPORTING_FAILED);
            setOemLicenseKeys([]);
            setOemFailedKeys([]);
            setError(
                t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.missingHeaders", {
                    missingHeaders: missingHeaders.join(", "),
                })
            );
            LOGGER.error(
                t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.missingHeaders", {
                    missingHeaders: missingHeaders.join(", "),
                })
            );
            setError(
                t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.missingHeaders", {
                    missingHeaders: missingHeaders.join(", "),
                })
            );
            throw new Error(
                t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.missingHeaders", {
                    missingHeaders: missingHeaders.join(", "),
                })
            );
        }

        const parsedData: SerialLicenseKey[] = [];
        if (lines.length == 1) {
            setDialogState(DialogState.IMPORTING_FAILED);
            setOemLicenseKeys([]);
            setOemFailedKeys([]);
            LOGGER.error(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.emptyRowError"));
            setError(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.emptyRowError"));
            throw new Error(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.emptyRowError"));
        }

        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();

            if (!line) {
                break;
            }
            const isEmptyRow = !line || line.split(",").every((cell) => !cell.trim());

            if (isEmptyRow) {
                setDialogState(DialogState.IMPORTING_FAILED);
                setOemLicenseKeys([]);
                setOemFailedKeys([]);
                LOGGER.error(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.emptyRowError"));
                setError(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.emptyRowError"));
                throw new Error(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.emptyRowError"));
            }

            const values = line.split(",").map((value) => value.trim());

            const rowData: Record<string, string> = {};
            headers.forEach((header, index) => {
                rowData[header] = values[index] || "";
            });

            const licenseKey = rowData.license_key;
            const identifier = rowData.identifier;
            const assignCount = Number(rowData.assign_count) || 0;
            const startDate = rowData.start_date;
            const expirationDate = rowData.expiration_date;
            const license_id = rowData.license_id;

            if (licenseKey && licenseKey.length > MAX_KEY_LENGTH) {
                if (!currentFailedKeys.includes(licenseKey)) {
                    currentFailedKeys.push(licenseKey);
                }
                const errorMessage = `Row ${i}: ${t("ViewOemKeys.table.licenseKeyLengthErrorMessageHighlight", { maxLength: MAX_KEY_LENGTH })}`;
                setError(errorMessage);
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                existingError.failingFields.push(
                    t("ViewOemKeys.table.licenseKeyLengthErrorMessageHighlight", { maxLength: MAX_KEY_LENGTH })
                );
                licenseKeyErrors.set(licenseKey, existingError);
            }

            if (!LICENSE_KEY_PATTERN.test(licenseKey)) {
                currentFailedKeys.push(licenseKey);
                const errorMessage = `Row ${i}: ${t("Licenses.bms.assignDialog.licenseKeyWrongCharactersError")}`;
                setError(errorMessage);
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                existingError.failingFields.push(t("ViewOemKeys.table.licenseKeyWrongCharactersHighlight"));
                licenseKeyErrors.set(licenseKey, existingError);
            }

            if (identifier.length > MAX_IDENTIFIER_LENGTH) {
                currentFailedKeys.push(licenseKey);
                const errorKey = t("ViewOemKeys.table.licenseKeyWrongIdentifierLengthHighlight", {
                    maxLength: MAX_IDENTIFIER_LENGTH,
                });
                const errorMessage = `Row ${i}: ${errorKey}`;
                setError(errorMessage);
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                existingError.failingFields.push(errorKey);
                licenseKeyErrors.set(licenseKey, existingError);
            }

            if (assignCount <= 0) {
                const errorMessage = `Row ${i}: ${t("Licenses.bms.assignDialog.assignMinError")}`;
                setError(errorMessage);
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                existingError.failingFields.push(t("ViewOemKeys.table.assignCountMessageHighlight"));
                licenseKeyErrors.set(licenseKey, existingError);
                if (!currentFailedKeys.includes(licenseKey)) {
                    currentFailedKeys.push(licenseKey);
                }
            }

            if (startDate == "") {
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                existingError.failingFields.push(t("ViewOemKeys.table.startDateIsEmptyMessageHighlight"));

                licenseKeyErrors.set(licenseKey, existingError);

                if (!currentFailedKeys.includes(licenseKey)) {
                    currentFailedKeys.push(licenseKey);
                }
            }

            if (!isUTCFormat(startDate)) {
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                existingError.failingFields.push(t("ViewOemKeys.table.startDateFormatErrorMessageHighlight"));
                licenseKeyErrors.set(licenseKey, existingError);
                if (!currentFailedKeys.includes(licenseKey)) {
                    currentFailedKeys.push(licenseKey);
                }
            }

            if (startDate && !isDateValid(getExpirationDate(), startDate)) {
                const errorMessage = `Row ${i}: ${t("Licenses.bms.assignDialog.startDateMaxError")}`;
                setError(errorMessage);
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                if (!existingError.failingFields.includes(t("ViewOemKeys.table.startDateMessageHighlight"))) {
                    existingError.failingFields.push(t("ViewOemKeys.table.startDateMessageHighlight"));
                }
                licenseKeyErrors.set(licenseKey, existingError);
                if (!currentFailedKeys.includes(licenseKey)) {
                    currentFailedKeys.push(licenseKey);
                }
            }

            if (expirationDate == "") {
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                existingError.failingFields.push(t("ViewOemKeys.table.expirationDateIsEmptyMessageHighlight"));
                licenseKeyErrors.set(licenseKey, existingError);
                if (!currentFailedKeys.includes(licenseKey)) {
                    currentFailedKeys.push(licenseKey);
                }
            }

            if (!isUTCFormat(expirationDate)) {
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                existingError.failingFields.push(t("ViewOemKeys.table.expirationDateFormatErrorMessageHighlight"));
                licenseKeyErrors.set(licenseKey, existingError);
                if (!currentFailedKeys.includes(licenseKey)) {
                    currentFailedKeys.push(licenseKey);
                }
            }

            if (expirationDate && !isDateValid(getExpirationDate(), expirationDate)) {
                const errorMessage = `Row ${i}: ${t("Licenses.bms.assignDialog.expirationDateMaxError")}`;
                setError(errorMessage);
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                if (!existingError.failingFields.includes(t("ViewOemKeys.table.expirationDateErrorMessageHighlight"))) {
                    existingError.failingFields.push(t("ViewOemKeys.table.expirationDateErrorMessageHighlight"));
                }
                licenseKeyErrors.set(licenseKey, existingError);
                if (!currentFailedKeys.includes(licenseKey)) {
                    currentFailedKeys.push(licenseKey);
                }
            }

            const startDateStr = `${new Date(startDate).getFullYear()}-${(new Date(startDate).getMonth() + 1).toString().padStart(2, "0")}-${new Date(startDate).getDate().toString().padStart(2, "0")}`;
            const expDateStr = `${new Date(expirationDate).getFullYear()}-${(new Date(expirationDate).getMonth() + 1).toString().padStart(2, "0")}-${new Date(expirationDate).getDate().toString().padStart(2, "0")}`;
            if (startDateStr > expDateStr) {
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                if (!existingError.failingFields.includes(t("ViewOemKeys.table.startDateMessageHighlight"))) {
                    existingError.failingFields.push(t("ViewOemKeys.table.startDateMessageHighlight"));
                }
                licenseKeyErrors.set(licenseKey, existingError);
                if (!currentFailedKeys.includes(licenseKey)) {
                    currentFailedKeys.push(licenseKey);
                }
            }

            if (!license_id || license_id != OEM_LICENSE_ID) {
                const existingError = licenseKeyErrors.get(licenseKey) || {
                    licenseKey: licenseKey,
                    failingFields: [],
                };
                existingError.failingFields.push(t("ViewOemKeys.table.licenseIdIsEmptyOrInvalidMessageHighlight"));
                licenseKeyErrors.set(licenseKey, existingError);
                if (!currentFailedKeys.includes(licenseKey)) {
                    currentFailedKeys.push(licenseKey);
                }
            }

            assignLicensesCountFromCsv += assignCount;

            const row: SerialLicenseKey = {
                license_key: licenseKey,
                identifier: identifier,
                assign_count: assignCount,
                start_date: startDate,
                expiration_date: expirationDate,
                license_id: license_id,
            };

            parsedData.push(row);
        }

        const failedLicenseKeys: SerialLicenseKey[] = parsedData.filter((each) =>
            currentFailedKeys.includes(each.license_key)
        );
        setOemFailedKeys(failedLicenseKeys);
        setOemLicenseKeys(parsedData);
        props.onLicenseImportCallback(
            parsedData,
            failedLicenseKeys,
            licenseKeyErrors,
            assignLicensesCountFromCsv,
            selectedFileListReference
        );
    };

    React.useEffect(() => {
        if (oemLicenseKeys.length > 0 || oemFailedKeys.length > 0) {
            oemFailedKeys.length > 0
                ? props.onTitleChange?.(t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.uploadingKeysFailed"))
                : props.onTitleChange?.(t("ViewOemKeys.title"));
            setDialogState(DialogState.VIEWING_LICENSE_KEYS);
        }
    }, [oemFailedKeys, oemLicenseKeys, assignLicensesCountFromCsv]);

    const createDialogLogic = React.useCallback(() => {
        switch (dialogState) {
            case DialogState.SELECTING_FILE:
                setError(undefined);
                return;

            case DialogState.LOADING_FILE: {
                if (selectedFileListReference.current === undefined) {
                    setError(t("Common.noFileSelected"));
                    LOGGER.error(t("Common.noFileSelected"));
                    return;
                }
                setError(undefined);
                const reader = new FileReader();
                reader.readAsText(selectedFileListReference.current[0]);
                reader.onload = () => {
                    try {
                        parseCSV(reader.result as string);
                    } catch {
                        setDialogState(DialogState.IMPORTING_FAILED);
                    }
                };
                return;
            }
            case DialogState.VIEWING_LICENSE_KEYS: {
                props.onClose();
                return;
            }
        }
    }, [dialogState]);

    function onDownloadFileClicked() {
        const headers = [
            t("ViewOemKeys.table.licenseKeyHeader"),
            t("ViewOemKeys.table.assignCountHeader"),
            t("ViewOemKeys.table.identifierHeader"),
            t("ViewOemKeys.table.startDateHeader"),
            t("ViewOemKeys.table.expirationDateHeader"),
            t("ViewOemKeys.table.licenseIdHeader"),
        ];
        const csvContent = [headers.join(",")].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "license_keys_template.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    const dispatch = () => {
        if (selectedFileListReference.current === undefined || selectedFileListReference.current?.length === 0) {
            setDialogState(DialogState.SELECTING_FILE);
        } else if (selectedFileListReference.current?.length === 1) {
            setDialogState(DialogState.LOADING_FILE);
        } else {
            setDialogState(DialogState.LOADING_FILES);
        }
    };

    const reset = () => {
        selectedFileListReference.current = undefined;
        setOemLicenseKeys([]);
        setError(undefined);
        setDialogState(DialogState.SELECTING_FILE);
    };
    const handleFileDrop = (fileList: FileList, event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        usageStatisticsService.sendEvent({
            category: Category.LICENSE_KEYS,
            action: Action.UPLOAD_KEYS_DRAGGING,
        });
        if (fileList.length > 0) {
            selectedFileListReference.current = Array.from(fileList);
        }
        dispatch();
    };

    function onFileInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        const { files } = event.target;
        usageStatisticsService.sendEvent({
            category: Category.LICENSE_KEYS,
            action: Action.UPLOAD_KEYS_SELECTING,
        });
        if (files !== null) {
            selectedFileListReference.current = Array.from(files);
        }
        dispatch();
    }

    function onSelectFileClicked() {
        fileInputRef.current?.click();
    }

    const loadingFile = (
        <LoadingView loadingMessage={t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.loadingMessage")} />
    );
    const loadingFiles = (
        <FailedView
            failureMessage={t("ImportOemSerialLicenseKeys.loadFiles.notSupported")}
            onUploadAnotherClicked={reset}
            onClose={props.onClose}
        />
    );
    const loadingFailed = (
        <FailedView
            failureMessage={typeof error === "string" ? t(error) : t("Common.failedToLoadFile")}
            onUploadAnotherClicked={reset}
            onClose={props.onClose}
        />
    );

    const selecting = (
        <SelectingView
            handleFileDrop={handleFileDrop}
            onFileInputChange={onFileInputChange}
            onSelectFileClicked={onSelectFileClicked}
            fileInputRef={fileInputRef}
            licenseKeysUpload={true}
            onDownloadFileClicked={onDownloadFileClicked}
        />
    );

    const succeeded = (
        <>
            {dialogState === DialogState.IMPORTING_SUCCEEDED && !hasSucceeded.current
                ? toast(
                      <SuccessIcon
                          successClass={layoutStyle.customToastSuccessIcon}
                          color={theme.contentBackgroundColor}
                          text={t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.keysImportedSuccessfully", {
                              successCount: oemLicenseKeys.length,
                          })}
                      />,
                      {
                          closeButton: (closeToastProps) => (
                              <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                          ),
                          className: layoutStyle.customToastSuccessMessage,
                      }
                  )
                : null}
        </>
    );

    const importingFailedErrorMessage = (
        <>
            {(error !== undefined && typeof error === "string") || typeof error === "object" ? (
                <>
                    {t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.failureMessageDetails", {
                        errors: error,
                    })}
                </>
            ) : (
                <>{t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.failureMessage")}</>
            )}
        </>
    );

    const importingFailed = (
        <ImportKeysFailedDialog
            failureMessage={importingFailedErrorMessage}
            onUploadAnotherClicked={reset}
            onClose={props.onClose}
            licenseKeysUpload={true}
            onDownloadFileClicked={onDownloadFileClicked}
        />
    );
    const stateToContent = new Map<DialogState, JSX.Element>([
        [DialogState.SELECTING_FILE, selecting],
        [DialogState.LOADING_FILE, loadingFile],
        [DialogState.LOADING_FILES, loadingFiles],
        [DialogState.LOADING_FILE_FAILED, loadingFailed],
        [DialogState.IMPORTING_FAILED, importingFailed],
        [DialogState.IMPORTING_SUCCEEDED, succeeded],
        [DialogState.VIEWING_LICENSE_KEYS, <></>],
        [DialogState.IMPORTING_LICENSE_KEYS, loadingFile],
    ]);
    return (
        <ImportFileDialog
            fileList={props.fileList}
            onClose={props.onClose}
            dialogLogic={createDialogLogic}
            stateToContent={stateToContent}
            stateDispatch={dispatch}
            dialogState={dialogState}
        />
    );
};

export default connector(ImportLicenseKeyDialog);
